/**
 * Offline prescription PDF generator.
 *
 * Mirrors the canonical server-side prescription PDF (kaagazy-backend
 * app/core/pdf.py :: generate_prescription_pdf) layout, fonts, colours,
 * and field ordering so a prescription printed offline looks like one
 * printed online — before or after finalisation.
 *
 * Intentional divergences from the server PDF (everything else matches):
 *   • No clinic logo: stored on S3, never cached locally. The header
 *     reserves the same 25 × 20 mm footprint and draws a blue placeholder
 *     square (same as the server's no-logo fallback path).
 *   • No real QR: the verify endpoint is unreachable when this path runs,
 *     so a same-size placeholder box is drawn with "Verify online when
 *     synced" caption.
 *
 * Inputs are the same shapes the page already has in memory from the
 * online and offline-fallback fetch paths, so call sites don't need
 * extra plumbing.
 */

import { jsPDF } from "jspdf";

import type {
  PatientInfo,
  PrescriptionResponse,
  VisitResponse,
} from "./diagnosis.service";
import { chartForPrint, type DentalPrint } from "../utils/dental-print";

export interface PrescriberInfo {
  first_name:           string;
  last_name:            string;
  name?:                string;
  specialization?:      string;
  specializations?:     string[];
  registration_number?: string;
  qualifications?:      string;
  department?:          string;
  display_id?:          string;
}

export interface ClinicHeader {
  name:      string;
  location?: string;
  address?:  string;
  phone?:    string;
  email?:    string;
}

export interface OfflinePdfInput {
  patient:      PatientInfo;
  visit:        VisitResponse;
  prescription: PrescriptionResponse;
  doctor:       PrescriberInfo | null;
  clinic:       ClinicHeader | null;
}

// ── Page metrics (A4 portrait, mm) — match pdf.py exactly ─────────────────
const PAGE_W    = 210;
const PAGE_H    = 297;
const MARGIN    = 15;
const CONTENT_W = PAGE_W - 2 * MARGIN;

// ── Colours (mirror pdf.py _BLUE / _DARK / _GREY / _LGREY) ────────────────
const BLUE  : [number, number, number] = [30, 80, 160];
const DARK  : [number, number, number] = [30, 30, 30];
const GREY  : [number, number, number] = [100, 100, 100];
const LGREY : [number, number, number] = [220, 220, 220];

export function generateOfflinePrescriptionPdf(input: OfflinePdfInput): string {
  const { patient, visit, prescription, doctor, clinic } = input;
  const doc = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });

  doc.setLineWidth(0.3);
  let y = MARGIN;

  // ── Header (logo placeholder + clinic name + address|phone|email) ───────
  y = drawHeader(doc, clinic, y);

  // ── Divider ─────────────────────────────────────────────────────────────
  y = divider(doc, y);

  // ── Patient / Doctor block ──────────────────────────────────────────────
  y = drawPatientBlock(doc, patient, doctor, prescription, visit, y);

  // ── Divider ─────────────────────────────────────────────────────────────
  y = divider(doc, y);

  // ── Rx symbol + date ────────────────────────────────────────────────────
  y = drawRxRow(doc, prescription.prescribed_at ?? visit.visit_date, y);

  // ── Medications ─────────────────────────────────────────────────────────
  for (let i = 0; i < prescription.medications.length; i++) {
    y = drawMedicationRow(doc, i + 1, prescription.medications[i], y);
  }

  // ── Dental findings + treatment plan (same wording as the server PDF) ──
  const dental = chartForPrint(visit.dental_chart);
  if (dental.findings.length) {
    y += 2;
    y = divider(doc, y);
    y += 1;
    y = labelled(doc, "Dental findings", dental.findings.join("\n"), y);
  }
  if (dental.plan.length) {
    y += 2;
    y = divider(doc, y);
    y += 1;
    y = drawDentalPlan(doc, dental.plan, y);
  }

  // ── Notes / Observation ─────────────────────────────────────────────────
  if (prescription.notes || prescription.observation) {
    y += 2;
    y = divider(doc, y);
    y += 1;
    if (prescription.notes) {
      y = labelled(doc, "Notes", prescription.notes, y);
    }
    if (prescription.observation) {
      y = labelled(doc, "Observation", prescription.observation, y);
    }
  }

  // ── Follow-up ────────────────────────────────────────────────────────────
  const followUpText = prescription.follow_up
    || (visit.follow_up_date
      ? new Date(visit.follow_up_date + "T00:00:00").toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })
      : null);
  if (followUpText) {
    y += 2;
    y = divider(doc, y);
    y += 1;
    y = labelled(doc, "Follow-up", followUpText, y);
  }

  // ── Footer (QR placeholder + Rx# + Digital Stamp box) ───────────────────
  drawFooter(doc, prescription.prescription_id, y);

  // ── Save ────────────────────────────────────────────────────────────────
  const safeName = `${patient.first_name} ${patient.last_name}`
    .replace(/[^a-zA-Z0-9_-]+/g, "_")
    .slice(0, 40) || "patient";
  const filename = `prescription-${safeName}-${prescription.prescription_id.slice(0, 8)}.pdf`;
  doc.save(filename);
  return filename;
}

// ─── Section drawers ──────────────────────────────────────────────────────

function drawHeader(doc: jsPDF, clinic: ClinicHeader | null, y: number): number {
  const logoW = 25;
  const logoH = 20;

  // Blue placeholder square — matches server fallback when no logo present.
  doc.setFillColor(...BLUE);
  doc.rect(MARGIN, y, logoW, logoH, "F");

  const textX = MARGIN + logoW + 4;

  // Clinic name
  doc.setTextColor(...BLUE);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.text(clinic?.name ?? "Clinic", textX, y + 7);

  // Address | Phone | Email (8pt grey, "  |  " separator like server)
  doc.setTextColor(...GREY);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  const parts = [clinic?.address ?? clinic?.location, clinic?.phone, clinic?.email]
    .filter(Boolean) as string[];
  if (parts.length > 0) {
    doc.text(parts.join("  |  "), textX, y + 12);
  }

  // Drop below the logo block (header_y + logo_h + 3)
  return y + logoH + 3;
}

function drawPatientBlock(
  doc:          jsPDF,
  patient:      PatientInfo,
  doctor:       PrescriberInfo | null,
  prescription: PrescriptionResponse,
  _visit:       VisitResponse,
  y:            number,
): number {
  doc.setTextColor(...DARK);
  doc.setFontSize(9);

  const row = (label: string, value: string): void => {
    doc.setFont("helvetica", "bold");
    doc.text(`${label}:`, MARGIN, y);
    doc.setFont("helvetica", "normal");
    doc.text(value, MARGIN + 30, y);
    y += 5;
  };

  const fullName   = `${patient.first_name} ${patient.last_name}`.trim() || "Unknown";
  const ageGender  = [computeAge(patient.date_of_birth), patient.gender]
    .filter(Boolean)
    .join(" / ") || "—";
  row("Patient", `${fullName}  (${ageGender})`);

  // Match server: display_id like "PAT-XXXX" or fallback to a UUID slice.
  const displayId = patient.display_id
    ?? (patient.patient_no ? `PAT-${String(patient.patient_no).padStart(4, "0")}` : patient.patient_id.slice(0, 8).toUpperCase());
  row("Patient ID", displayId);

  if (doctor) {
    let rawDocName = doctor.name?.trim();
    if (!rawDocName && (doctor.first_name || doctor.last_name)) {
      rawDocName = `${doctor.first_name || ""} ${doctor.last_name || ""}`.trim();
    }

    let docLine = "";
    if (rawDocName && rawDocName !== "-" && rawDocName !== "—") {
      docLine = /^(?:dr\.?|doctor)\s+/i.test(rawDocName)
        ? `Dr. ${rawDocName.replace(/^(?:(?:dr\.?|doctor)\s+)+/i, "").trim()}`
        : `Dr. ${rawDocName}`;
    }

    if (doctor.qualifications) {
      docLine = docLine ? `${docLine} (${doctor.qualifications})` : `(${doctor.qualifications})`;
    }

    let specTokens: string[] = [];
    if (doctor.specializations && doctor.specializations.length > 0) {
      specTokens = doctor.specializations.filter(Boolean).map(s => s.trim());
    } else if (doctor.specialization) {
      specTokens = doctor.specialization.split(",").map(s => s.trim()).filter(Boolean);
    }
    if (doctor.department) {
      const deptLower = doctor.department.trim().toLowerCase();
      specTokens = specTokens.filter(s => s.toLowerCase() !== deptLower);
    }
    const spec = specTokens.join(", ");

    if (doctor.department && spec) {
      docLine += (docLine ? "  |  " : "") + `Dept: ${doctor.department}  ·  ${spec}`;
    } else if (doctor.department) {
      docLine += (docLine ? "  |  " : "") + `Dept: ${doctor.department}`;
    } else if (spec) {
      docLine += (docLine ? "  |  " : "") + `${spec}`;
    }

    if (doctor.display_id) {
      docLine += (docLine ? "  |  " : "") + `ID: ${doctor.display_id}`;
    }
    if (doctor.registration_number) {
      const cleanReg = doctor.registration_number.replace(
        /^(?:(?:NMC|MCI)\s*[:-]?\s*)?(?:Registration|Reg\.?)\s*(?:No\.?|Number)?\s*[:-]?\s*/i,
        ""
      ).trim();
      if (cleanReg) {
        docLine += (docLine ? "  |  " : "") + `NMC Reg. No: ${cleanReg}`;
      }
    }
    if (docLine) {
      row("Doctor", docLine);
    }
  }

  const validUntil = (prescription as { valid_until?: string }).valid_until;
  if (validUntil) {
    const d = new Date(validUntil);
    if (!isNaN(d.getTime())) {
      row("Valid until", formatDay(d));
    }
  }

  return y;
}

function drawRxRow(doc: jsPDF, prescribedAt: string | undefined, y: number): number {
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.setTextColor(...BLUE);
  doc.text("Rx", MARGIN, y + 7);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...GREY);
  const dateStr = prescribedAt && !isNaN(new Date(prescribedAt).getTime())
    ? formatDay(new Date(prescribedAt))
    : "—";
  doc.text(dateStr, PAGE_W - MARGIN, y + 7, { align: "right" });

  doc.setTextColor(...DARK);
  return y + 10;
}

function drawMedicationRow(
  doc: jsPDF,
  idx: number,
  med: PrescriptionResponse["medications"][number],
  y:   number,
): number {
  y = pageBreakIfNeeded(doc, y, 16);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(...DARK);
  doc.text(`${idx}. ${med.name}  -  ${med.dosage ?? ""}`, MARGIN, y);
  y += 6;

  const parts = [
    med.frequency,
    med.duration,
    med.route,
    med.quantity ? `Qty: ${med.quantity}` : null,
  ].filter(Boolean) as string[];
  if (parts.length > 0) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...GREY);
    doc.text(parts.join("  |  "), MARGIN + 6, y);
    y += 5;
  }

  if (med.instructions) {
    doc.setFont("helvetica", "italic");
    doc.setFontSize(8);
    doc.setTextColor(...GREY);
    y = drawWrapped(doc, med.instructions, MARGIN + 6, y, CONTENT_W - 6, 4);
  }

  doc.setTextColor(...DARK);
  return y + 2;
}

function drawDentalPlan(doc: jsPDF, rows: DentalPrint["plan"], y: number): number {
  y = pageBreakIfNeeded(doc, y, 12);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...DARK);
  doc.text("Dental treatment plan:", MARGIN, y);
  y += 5;
  doc.setFontSize(8);
  doc.setTextColor(...GREY);
  doc.text("TOOTH / AREA", MARGIN, y);
  doc.text("PROCEDURE", MARGIN + 42, y);
  doc.text("SITTINGS", PAGE_W - MARGIN, y, { align: "right" });
  y += 4;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...DARK);
  rows.forEach((r, i) => {
    y = pageBreakIfNeeded(doc, y, 5);
    doc.text(`${i + 1}. ${r.where}`, MARGIN, y);
    const label = doc.splitTextToSize(r.label, CONTENT_W - 42 - 20)[0] as string;
    doc.text(label, MARGIN + 42, y);
    doc.text(String(r.sittings), PAGE_W - MARGIN, y, { align: "right" });
    y += 5;
  });
  return y;
}

function labelled(doc: jsPDF, label: string, text: string, y: number): number {
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...DARK);
  doc.text(`${label}:`, MARGIN, y);

  doc.setFont("helvetica", "normal");
  return drawWrapped(doc, text, MARGIN + 30, y, CONTENT_W - 30, 5);
}

function drawFooter(doc: jsPDF, prescriptionId: string, y: number): number {
  // Reserve ~40mm at bottom; push down if current y is too close.
  let footerY = PAGE_H - MARGIN - 40;
  if (y > footerY - 5) footerY = y + 5;
  if (footerY + 35 > PAGE_H - MARGIN) {
    doc.addPage();
    footerY = MARGIN;
  }

  divider(doc, footerY);

  const qrSize    = 30;
  const stampSize = 30;

  // QR placeholder box (left) — same footprint as the server QR.
  doc.setDrawColor(...LGREY);
  doc.setLineWidth(0.3);
  doc.rect(MARGIN, footerY + 3, qrSize, qrSize, "D");

  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(...GREY);
  doc.text("Verify online", MARGIN + qrSize / 2, footerY + 3 + qrSize / 2 - 1, { align: "center" });
  doc.text("when synced",  MARGIN + qrSize / 2, footerY + 3 + qrSize / 2 + 3, { align: "center" });

  // Rx# centred between the QR and stamp boxes.
  const stampX = PAGE_W - MARGIN - stampSize;
  const midX   = MARGIN + qrSize + 10;
  const midW   = stampX - midX - 10;
  doc.setFontSize(7);
  doc.setTextColor(...GREY);
  doc.text(
    `Rx# ${prescriptionId.toUpperCase().slice(0, 8)}`,
    midX + midW / 2,
    footerY + 3 + stampSize / 2 - 3,
    { align: "center" },
  );

  // Digital stamp box (right) — matches server design.
  doc.setDrawColor(...BLUE);
  doc.setLineWidth(0.5);
  doc.rect(stampX, footerY + 3, stampSize, stampSize, "D");
  doc.setFontSize(7);
  doc.setTextColor(...BLUE);
  doc.text("Digital Stamp", stampX + stampSize / 2, footerY + 3 + stampSize / 2 - 3, { align: "center" });

  return footerY + 3 + stampSize;
}

// ─── Helpers ──────────────────────────────────────────────────────────────

function divider(doc: jsPDF, y: number): number {
  doc.setDrawColor(...LGREY);
  doc.setLineWidth(0.3);
  doc.line(MARGIN, y, PAGE_W - MARGIN, y);
  return y + 3;
}

function drawWrapped(
  doc:      jsPDF,
  text:     string,
  x:        number,
  y:        number,
  maxWidth: number,
  lineGap:  number,
): number {
  const lines = doc.splitTextToSize(text, maxWidth) as string[];
  for (const line of lines) {
    y = pageBreakIfNeeded(doc, y, lineGap);
    doc.text(line, x, y);
    y += lineGap;
  }
  return y;
}

function pageBreakIfNeeded(doc: jsPDF, y: number, needed: number): number {
  if (y + needed > PAGE_H - 20) {
    doc.addPage();
    return MARGIN;
  }
  return y;
}

function computeAge(dob: string | undefined): string {
  if (!dob) return "";
  const d = new Date(dob);
  if (isNaN(d.getTime())) return "";
  const ms    = Date.now() - d.getTime();
  const years = Math.floor(ms / (365.25 * 24 * 60 * 60 * 1000));
  return years > 0 ? `${years} Y` : "";
}

function formatDay(d: Date): string {
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${String(d.getDate()).padStart(2, "0")} ${months[d.getMonth()]} ${d.getFullYear()}`;
}
